import { useEffect, useRef, useState } from 'react';
import { Alert, App, Form, Input, InputNumber, Modal, Select, Typography } from 'antd';
import type {
  AdjustCustomerBalanceBody,
  BalanceAdjustmentReason,
} from '@goelectric/shared-types';
import { createIdempotencyKey, customerApi } from '../api/customerApi';
import { BALANCE_REASON_LABELS, formatCop, formatSignedCop } from '../utils/format';
import { describeCustomerApiError } from '../utils/customerErrors';

const REASON_OPTIONS = (
  ['MANUAL_CREDIT', 'MANUAL_DEBIT', 'REFUND', 'CORRECTION', 'PROMOTION'] as const
).map((value) => ({ value, label: BALANCE_REASON_LABELS[value] }));

interface BalanceAdjustmentFormState {
  amountCop: number;
  reason: BalanceAdjustmentReason;
  note?: string;
}

export interface BalanceAdjustModalProps {
  open: boolean;
  customerId: string;
  /** Saldo actual (COP) para mostrar el resultado previsto. */
  currentBalanceCop: number;
  onClose: () => void;
  /** Refresca el detalle tras un ajuste exitoso. */
  onAdjusted: () => void;
}

/**
 * Modal de ajuste de saldo auditable (US-2.3). El monto lleva signo (positivo=abono,
 * negativo=débito), se exige un motivo y se permite una nota interna. Es el endpoint
 * dedicado del contrato: el saldo no se modifica por el PATCH del cliente.
 */
export function BalanceAdjustModal({
  open,
  customerId,
  currentBalanceCop,
  onClose,
  onAdjusted,
}: BalanceAdjustModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm<BalanceAdjustmentFormState>();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const amount = Form.useWatch('amountCop', form);
  /**
   * Clave de idempotencia de la **intención de ajuste** actual (no del submit). Se
   * genera al abrir/preparar el modal y se reutiliza en todos los reintentos del mismo
   * submit (fallo de red, refresh de sesión, etc.). Se anula al cancelar/cerrar y tras
   * un ajuste exitoso, de modo que la siguiente intención use una clave nueva.
   */
  const idempotencyKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      form.resetFields();
      idempotencyKeyRef.current = createIdempotencyKey();
    } else {
      idempotencyKeyRef.current = null;
    }
  }, [open, form]);

  const handleCancel = () => {
    idempotencyKeyRef.current = null;
    onClose();
  };

  const handleFinish = async (values: BalanceAdjustmentFormState) => {
    setSubmitting(true);
    setError(null);
    const body: AdjustCustomerBalanceBody = {
      amountCop: values.amountCop,
      reason: values.reason,
      note: values.note?.trim() || undefined,
    };
    try {
      // Si la clave se anuló (p. ej. el modal sigue montado abierto tras un éxito), se
      // regenera para esta nueva intención; en un reintento se conserva la misma.
      const idempotencyKey =
        idempotencyKeyRef.current ?? (idempotencyKeyRef.current = createIdempotencyKey());
      const result = await customerApi.adjustBalance(customerId, body, idempotencyKey);
      // Éxito: la intención queda consumida; un ajuste posterior debe llevar clave nueva.
      idempotencyKeyRef.current = null;
      message.success(`Ajuste aplicado. Saldo actual: ${formatCop(result.balanceAfterCop)}.`);
      onAdjusted();
      onClose();
    } catch (caught) {
      setError(describeCustomerApiError(caught, 'No se pudo aplicar el ajuste.').message);
    } finally {
      setSubmitting(false);
    }
  };

  const projected =
    typeof amount === 'number' && !Number.isNaN(amount) ? currentBalanceCop + amount : undefined;

  return (
    <Modal
      open={open}
      title="Ajustar saldo"
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText="Aplicar ajuste"
      cancelText="Cancelar"
      confirmLoading={submitting}
      destroyOnHidden
    >
      {error && (
        <Alert
          type="error"
          showIcon
          role="alert"
          message="No se pudo aplicar el ajuste"
          description={error}
          style={{ marginBottom: 16 }}
        />
      )}

      <Typography.Paragraph type="secondary">
        Saldo actual: <Typography.Text strong>{formatCop(currentBalanceCop)}</Typography.Text>. El
        ajuste queda registrado con motivo y autor.
      </Typography.Paragraph>

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          label="Monto (COP)"
          name="amountCop"
          extra="Positivo suma saldo (abono); negativo lo descuenta (débito)."
          rules={[
            { required: true, message: 'Ingresa el monto del ajuste.' },
            {
              validator: (_, value: number) =>
                value === 0 || value === undefined
                  ? Promise.reject(new Error('El monto no puede ser cero.'))
                  : Promise.resolve(),
            },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            precision={0}
            step={1000}
            placeholder="Ej. 20000 (abono) o -5000 (débito)"
            aria-label="Monto del ajuste en pesos"
          />
        </Form.Item>

        <Form.Item
          label="Motivo"
          name="reason"
          rules={[{ required: true, message: 'Selecciona el motivo del ajuste.' }]}
        >
          <Select placeholder="Selecciona el motivo" options={REASON_OPTIONS} />
        </Form.Item>

        <Form.Item label="Nota (interna)" name="note">
          <Input.TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder="Describe el motivo del ajuste (visible en auditoría)."
          />
        </Form.Item>
      </Form>

      {projected !== undefined && (
        <Typography.Text type="secondary">
          Saldo resultante: <Typography.Text strong>{formatCop(projected)}</Typography.Text> (
          {formatSignedCop(amount as number)}).
        </Typography.Text>
      )}
    </Modal>
  );
}
